import {
    BoldPlugin,
    H1Plugin,
    H2Plugin,
    H3Plugin,
    H4Plugin,
    ItalicPlugin,
    SuperscriptPlugin,
    UnderlinePlugin,
    BlockquotePlugin,
} from '@platejs/basic-nodes/react';
import { LinkPlugin } from '@platejs/link/react';
import { ImagePlugin, MediaEmbedPlugin } from '@platejs/media/react';
import {
    TableCellHeaderPlugin,
    TableCellPlugin,
    TablePlugin,
    TableRowPlugin,
} from '@platejs/table/react';
import { HtmlPlugin, TrailingBlockPlugin } from 'platejs';
import { createPlatePlugin, ParagraphPlugin } from 'platejs/react';

import { sanitizeIframeHtml } from 'common/components/Embed';
import { EditorBlockquote } from 'common/components/ContentEditor/elements/blockquote';
import { EditorEmbed } from 'common/components/ContentEditor/elements/embed';
import { EditorFile } from 'common/components/ContentEditor/elements/file';
import { EditorImage } from 'common/components/ContentEditor/elements/image';
import { EditorTable } from 'common/components/ContentEditor/elements/table';
import { EditorComponent } from 'common/components/ContentEditor/components/EditorComponent';
import { EditorLeaf } from 'common/components/ContentEditor/components/EditorLeaf';
import { EditorLink } from 'common/components/ContentEditor/elements/link';
import { options } from 'common/components/ContentEditor/options';
import {
    getSafeContentUrl,
    isUrl,
} from 'common/components/ContentEditor/utils/editorHelpers';

import { ExitBreakShortcutsPlugin } from './exitBreakShortcutsPlugin';
import { withNormalize } from './normalize/withNormalize';
import { withListEnter } from './withListEnter';
import { withBlockquoteEnter } from './withBlockquoteEnter';

const resetBlockRules = {
    break: { empty: 'reset' as const },
    delete: { start: 'reset' as const, empty: 'reset' as const },
};

const parseSlateUrl = (element: HTMLElement, type: string) => {
    const url = getSafeContentUrl(
        element.getAttribute('data-slate-url') || undefined
    );

    if (!url) return;

    return { type, url };
};

const createBlockPlugin = (type: string, as?: string) =>
    createPlatePlugin({
        key: type,
        node: { isElement: true, type },
    }).withComponent((props) => (
        <EditorComponent {...props} as={as as never} />
    ));

const NormalizePlugin = createPlatePlugin({
    key: 'normalize',
}).overrideEditor(withNormalize as never);

const ListEnterPlugin = createPlatePlugin({
    key: 'list-enter',
}).overrideEditor(withListEnter as never);

const BlockquoteEnterPlugin = createPlatePlugin({
    key: 'blockquote-enter',
}).overrideEditor(withBlockquoteEnter as never);

export const createEditorPlugins = () => [
    ParagraphPlugin.withComponent((props) => (
        <EditorComponent {...props} as="Typography" />
    )),
    H1Plugin.withComponent((props) => (
        <EditorComponent {...props} as="Typography" variant="h1" />
    )),
    H2Plugin.withComponent((props) => (
        <EditorComponent {...props} as="Typography" variant="h2" />
    )),
    H3Plugin.withComponent((props) => (
        <EditorComponent {...props} as="Typography" variant="h3" />
    )),
    H4Plugin.withComponent((props) => (
        <EditorComponent {...props} as="Typography" variant="h4" />
    )),
    BlockquotePlugin.withComponent(EditorBlockquote),
    BoldPlugin.withComponent((props) => <EditorLeaf {...props} as="strong" />),
    ItalicPlugin.withComponent((props) => <EditorLeaf {...props} as="em" />),
    UnderlinePlugin.withComponent((props) => <EditorLeaf {...props} as="u" />),
    SuperscriptPlugin.withComponent((props) => (
        <EditorLeaf {...props} as="sup" />
    )),
    LinkPlugin.configure({
        node: { component: EditorLink },
        options: { isUrl },
    }),
    createBlockPlugin(options.ul.type, 'ul'),
    createBlockPlugin(options.ol.type, 'ol'),
    createBlockPlugin(options.li.type, 'li'),
    createBlockPlugin(options.tbody.type, 'TableBody'),
    TablePlugin.withComponent(EditorTable),
    TableRowPlugin.withComponent((props) => (
        <EditorComponent {...props} as="TableRow" />
    )),
    TableCellPlugin.configure({
        rules: { break: { default: 'lineBreak' } },
    }).withComponent((props) => <EditorComponent {...props} as="TableCell" />),
    TableCellHeaderPlugin.withComponent((props) => (
        <EditorComponent {...props} as="TableHead" />
    )),
    ImagePlugin.configure({
        node: { isVoid: false },
        parsers: {
            html: {
                deserializer: {
                    rules: [
                        {
                            validClassName: options.img.rootProps.className,
                            validNodeName: '*',
                        },
                    ],
                    parse: ({ element, type }) => ({
                        type,
                        url:
                            element.getAttribute('data-slate-url') ||
                            element.getAttribute('src'),
                    }),
                },
            },
        },
        rules: { break: { default: 'exit' } },
    }).withComponent(EditorImage),
    MediaEmbedPlugin.configure({
        parsers: {
            html: {
                deserializer: {
                    rules: [
                        { validNodeName: 'IFRAME' },
                        {
                            validClassName:
                                options.media_embed.rootProps.className,
                            validNodeName: '*',
                        },
                    ],
                    parse: ({ element, type }) => {
                        // Re-sanitize regardless of source: a pasted
                        // data-slate-html attribute is just as untrusted as
                        // a bare <iframe> — it could be forged by a paste
                        // source mimicking our own markup.
                        const rawHtml =
                            element.getAttribute('data-slate-html') ||
                            (element.nodeName === 'IFRAME'
                                ? element.outerHTML
                                : undefined);
                        const html = rawHtml
                            ? sanitizeIframeHtml(rawHtml)
                            : undefined;

                        if (html) return { type, html };

                        const url =
                            element.getAttribute('data-slate-url') ||
                            element.getAttribute('src');

                        if (!url) return;

                        return { type, url };
                    },
                },
            },
        },
        rules: { break: { default: 'exit' } },
    }).withComponent(EditorEmbed),
    createPlatePlugin({
        key: options.file.type,
        node: {
            isElement: true,
            type: options.file.type,
        },
        parsers: {
            html: {
                deserializer: {
                    parse: ({ element, type }) => parseSlateUrl(element, type),
                },
            },
        },
        rules: {
            break: { default: 'exit', empty: 'reset' },
            delete: resetBlockRules.delete,
        },
    }).withComponent(EditorFile),
    TrailingBlockPlugin.configure({
        options: { type: options.p.type, level: 0 },
    }),
    ExitBreakShortcutsPlugin,
    ListEnterPlugin,
    BlockquoteEnterPlugin,
    NormalizePlugin,
    HtmlPlugin,
];
